import React, {useState, useCallback} from "react";
import {FileText, X, Calendar, Download} from "lucide-react";
import {DatePicker, Select} from "antd";
import dayjs from "dayjs";
import {APIv1} from "../../../api";
import {
    generateDynamicExcelFileName
} from "../../../pages/Orders/orderUtils/generateExcelFileName";
import {encodeDate} from "../../../utils/date/dateEncode";
import InnSelect from "../../Orders/OrderInnSelect";
import "./ExportExcelButton.css";
import {getServerOrderType} from "../../../pages/Orders/orderUtils/generateOrderType";

const {RangePicker} = DatePicker;

const ExportExcelButton = ({
                               t,
                               userData,
                               marketOptions,
                               exportUrl,
                               dateParamNames = {start: "begin_date", end: "end_date"},
                               buttonLabel,
                               fileExtension = "xlsx",
                               dateFormat = "YYYY-MM-DD HH:mm:ss",
                               prefixType = "Checks",
                               orderType = "order",
                               includeInn = true
                           }) => {
    const [isUserStaff, setIsUserStaff] = useState(userData?.data?.is_admin || false);
    const [loading, setLoading] = useState(false);
    const [innError, setInnError] = useState(false);
    const [datePickerVisible, setDatePickerVisible] = useState(false);
    const [dateRange, setDateRange] = useState([
        dayjs().startOf("day").set("second", 1),
        dayjs().endOf("day").set("second", 59),
    ]);
    const [selectedInn, setSelectedInn] = useState(null);
    const [selectedMarketId, setSelectedMarketId] = useState(null);

    const triggerInnError = () => {
        setInnError(true);
        setTimeout(() => setInnError(false), 5000); // auto-hide after 5s
    };

    const handleChangePeriod = (value) => {
        if (value) {
            setDateRange([value[0].set("second", 1), value[1].set("second", 59)]);
        }
    };

    const handleSelectChange = (value) => {
        setSelectedInn(value);
        if (value) setInnError(false);
    };

    const handleMarketSelectChange = (value) => {
        console.log("Selected Market ID:", value);
        setSelectedMarketId(value);
        if (value) setInnError(false);
    };

    const exportToExcel = useCallback(async () => {
        if (loading || !userData?.token || !dateRange[0] || !dateRange[1]) return;

        if (isUserStaff && includeInn && !selectedInn) {
            triggerInnError();
            return;
        }

        setInnError(false);
        setLoading(true);

        const lang = localStorage.getItem("i18nextLng") || "en";
        const beginDate = encodeDate(dayjs(dateRange[0]));
        const endDate = encodeDate(dayjs(dateRange[1]));

        try {
            const generatedServerOrderType = getServerOrderType(orderType)
            let url = `${exportUrl}?${dateParamNames.start}=${beginDate}&${dateParamNames.end}=${endDate}&lang=${lang}&order_type=${generatedServerOrderType}`;
            if (includeInn && selectedInn) {
                url += `&company_inn=${selectedInn}`;
            }
            if (selectedMarketId !== null && selectedMarketId !== undefined) {
                url += `&market_id=${selectedMarketId}`;
            }

            const response = await APIv1.get(url, {
                headers: {Authorization: `Token ${userData.token}`},
                responseType: "blob",
            });

            if (response.status !== 200) {
                throw new Error(`Unexpected response: ${response.status}`);
            }

            let filename = `${generateDynamicExcelFileName(lang, prefixType)}.${fileExtension}`;
            filename = decodeURIComponent(filename);

            // Use the correct MIME type for .xlsx
            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const urlObj = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = urlObj;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => URL.revokeObjectURL(urlObj), 1000);
        } catch (error) {
            console.error("Excel export failed:", error);
            alert(t("pages.orders.errors.errorText1") || "Failed to export Excel file.");
        } finally {
            setLoading(false);
        }
    }, [loading, userData?.token, dateRange, t, selectedInn, selectedMarketId, isUserStaff, exportUrl, dateParamNames, fileExtension, includeInn, prefixType]);

    return (
        <div className="excel-export-container">
            <button className="excel-button" onClick={() => setDatePickerVisible(!datePickerVisible)}>
                <FileText size={15}/>
                <span>{buttonLabel}</span>
            </button>

            {datePickerVisible && (
                <div className="date-picker-dropdown">
                    <div className="date-picker-header">
                        <Calendar size={16}/>
                        <span>{t("pages.orders.listColumns.importExcel3")}</span>
                        <button className="close-button" onClick={() => setDatePickerVisible(false)}>
                            <X size={16}/>
                        </button>
                    </div>

                    <div className="date-picker-content">
                        <RangePicker
                            showTime={dateFormat.includes("HH:mm:ss")}
                            allowClear={false}
                            onChange={handleChangePeriod}
                            value={dateRange}
                            format={dateFormat}
                            className="custom-range-picker"
                        />

                        {isUserStaff && includeInn ? (
                            <div className="inn-select-container">
                                <InnSelect
                                    onSelectChange={handleSelectChange}
                                    userData={userData}
                                    placeholder={t("pages.orders.listColumns.placeholder1")}
                                />
                            </div>
                        ) : (
                            <div className="inn-select-container">
                                <Select
                                    virtual={false}
                                    style={{width: 410}}
                                    options={marketOptions}
                                    placeholder="Select Market"
                                    onChange={handleMarketSelectChange}
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                />
                            </div>
                        )}
                        {innError && (
                            <div className="inn-select-error">
                                <span>{t("pages.orders.errors.errorText2")}</span>
                                <button className="hide-error-button" onClick={() => setInnError(false)}>
                                    <X size={14}/>
                                </button>
                            </div>
                        )}
                    </div>

                    <button
                        className="export-action-button"
                        onClick={exportToExcel}
                        disabled={!dateRange[0] || !dateRange[1] || loading}
                    >
                        <Download size={16}/>
                        <span>
              {loading ? t("pages.orders.actionText.excel.text1") : t("pages.orders.listColumns.importExcel2")}
            </span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default ExportExcelButton;